import type { ApplicantCard } from '@/hooks/use-opportunity-applicants'
import { isApplicantWithinNewWindow } from '@/lib/applicant-freshness'
import { matchBand } from '@/lib/match-bands'
import { calendarDaysSinceIso } from '@/lib/time-format'

export type SmartAnalysisFinding =
  | {
      kind: 'new_applicants'
      count: number
    }
  | {
      kind: 'strong_invited'
      applicant: ApplicantCard
      percent: number
      daysInvited: number
    }
  | {
      kind: 'unanalysed'
      count: number
    }
  | {
      kind: 'clear'
    }

export function buildSmartAnalysisFindings(
  applicants: ApplicantCard[],
  previousRunAt?: string | null,
): SmartAnalysisFinding[] {
  const findings: SmartAnalysisFinding[] = []

  const newApplicantCount = applicants.filter((applicant) => {
    if (applicant.status !== 'applied') return false
    const appliedAt = new Date(applicant.applied_at).getTime()
    if (Number.isNaN(appliedAt)) return false
    if (previousRunAt) {
      const previousRunMs = new Date(previousRunAt).getTime()
      if (Number.isNaN(previousRunMs)) return false
      return appliedAt > previousRunMs
    }
    return isApplicantWithinNewWindow(applicant.applied_at)
  }).length

  if (newApplicantCount > 0) {
    findings.push({ kind: 'new_applicants', count: newApplicantCount })
  }

  for (const applicant of applicants) {
    if (applicant.status !== 'invited') continue
    if (typeof applicant.match_score !== 'number') continue
    if (matchBand(applicant.match_score) !== 'strong') continue
    findings.push({
      kind: 'strong_invited',
      applicant,
      percent: Math.round(applicant.match_score * 100),
      daysInvited: calendarDaysSinceIso(applicant.applied_at),
    })
  }

  const unanalysedCount = applicants.filter((applicant) => applicant.match_score == null).length
  if (unanalysedCount > 0) {
    findings.push({ kind: 'unanalysed', count: unanalysedCount })
  }

  if (findings.length === 0) {
    findings.push({ kind: 'clear' })
  }

  return findings
}
